(() => {
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!('IntersectionObserver' in window) || window.Shopify?.designMode) return;
  if (document.body.classList.contains('mdc-page-index') && window.matchMedia('(max-width: 749px) and (hover: none) and (pointer: coarse)').matches) return;

  const selector = [
    '.md-section--products .md-product',
    '.md-editorial__intro',
    '.md-editorial__cards .md-use',
    '.md-signature > *',
    '#MainContent .resource-list__item:has(product-card)',
    '.mdc-product-story__media',
    '.mdc-product-faq__media',
    '.mdc-client-voices__featured-media',
    '.mdc-product-testimonial__media',
    '.mdc-upcoming__card',
    '.product-information__media .media-gallery__grid .product-media',
    '.product-information__media slideshow-slide .product-media'
  ].join(',');

  function finish(item) {
    item.classList.remove('mdc-reveal--entering');
  }


  function loadVisibleGalleryImages(item, measuredRect) {
    if (!item.closest('.product-information__media')) return;
    const rect = measuredRect || item.getBoundingClientRect();
    if (!rect.width || !rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) return;
    item.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  }

  function reveal(item, animate = true) {
    loadVisibleGalleryImages(item);
    observer.unobserve(item);
    item.dataset.mdcRevealState = 'visible';
    item.classList.add('mdc-reveal--visible');
    if (!animate || motionPreference.matches || item.contains(document.activeElement)) {
      finish(item);
      return;
    }
    item.classList.add('mdc-reveal--entering');
    item.addEventListener('animationend', () => finish(item), { once: true });
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      reveal(entry.target, entry.boundingClientRect.bottom > 0);
    });
  }, { rootMargin: '0px 0px -24px 0px', threshold: 0.01 });

  function prepare(root = document) {
    const items = [];

    if (root instanceof Element && root.matches(selector)) items.push(root);
    if (root.querySelectorAll) items.push(...root.querySelectorAll(selector));

    // Read geometry in one phase, then write classes/styles. Alternating reads
    // and writes per card forces layout repeatedly on image-rich collections.
    const prepared = items.filter((item) => {
      return !item.dataset.mdcRevealState && !item.closest('zoom-dialog, dialog, details:not([open])');
    }).map((item) => ({ item, rect: item.getBoundingClientRect() }));

    const siblingOrders = new WeakMap();
    prepared.forEach(({ item, rect }) => {
      if (item.dataset.mdcRevealState || item.closest('zoom-dialog, dialog, details:not([open])')) return;
      if (!rect.width || !rect.height) return;
      if (motionPreference.matches || rect.top < window.innerHeight || item.contains(document.activeElement)) {
        loadVisibleGalleryImages(item, rect);
        item.dataset.mdcRevealState = 'immediate';
        return;
      }

      const gallery = item.closest('.product-information__media');
      if (gallery) {
        const tile = item.closest('li, slideshow-slide');
        if (tile === tile?.parentElement?.firstElementChild) {
          item.dataset.mdcRevealState = 'immediate';
          return;
        }
        item.style.setProperty('--mdc-reveal-offset', '0px');
      }

      const parent = item.parentElement;
      if (!siblingOrders.has(parent)) {
        const orders = new Map();
        [...parent.children].filter((sibling) => sibling.matches(selector)).forEach((sibling, index) => {
          orders.set(sibling, index);
        });
        siblingOrders.set(parent, orders);
      }
      const order = siblingOrders.get(parent).get(item) ?? -1;
      item.style.setProperty('--mdc-reveal-delay', `${Math.max(0, order % 4) * 65}ms`);
      item.dataset.mdcRevealState = 'waiting';
      item.classList.add('mdc-reveal');
      observer.observe(item);
    });
  }

  function topLevelRoots(nodes) {
    return Array.from(nodes).filter(function (candidate) {
      if (!candidate.isConnected) return false;
      for (var parent = candidate.parentElement; parent; parent = parent.parentElement) {
        if (nodes.has(parent)) return false;
      }
      return true;
    });
  }

  let started = false;

  function start() {
    if (started) return;
    started = true;
    prepare();
    const main = document.getElementById('MainContent');
    if (!main) return;
    const pendingRoots = new Set();
    let frame = 0;
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.isConnected) pendingRoots.add(node);
        });
      });
      if (!pendingRoots.size || frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const roots = topLevelRoots(pendingRoots);
        pendingRoots.clear();
        roots.forEach((node) => prepare(node));
      });
    }).observe(main, { childList: true, subtree: true });
  }

  function scheduleStart() {
    if (!document.body.classList.contains('mdc-page-index') || document.documentElement.classList.contains('shopify-design-mode')) {
      start();
      return;
    }
    const afterLoad = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout: 1800 });
      } else {
        window.setTimeout(start, 150);
      }
    };
    if (document.readyState === 'complete') {
      afterLoad();
    } else {
      window.addEventListener('load', afterLoad, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleStart, { once: true });
  } else {
    scheduleStart();
  }

  document.addEventListener('shopify:section:load', (event) => prepare(event.target));
  document.addEventListener('focusin', (event) => {
    const item = event.target.closest('.mdc-reveal');
    if (item) reveal(item, false);
  });
  motionPreference.addEventListener('change', () => {
    if (!motionPreference.matches) return;
    document.querySelectorAll('.mdc-reveal').forEach((item) => reveal(item, false));
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('.mdc-reveal--entering').forEach(finish);
    prepare();
  });
})();
